import { Controller } from "@hotwired/stimulus"

// Controller pour afficher un modal de détail utilisateur
// Suit le même pattern que confirmation_modal_controller (clone de template)
//
// Le contenu cloné est inséré dans document.body (hors du scope Stimulus),
// donc tous les événements sont attachés par addEventListener, pas data-action.
//
// Usage:
//   <div data-controller="user-detail-modal">
//     <button data-action="click->user-detail-modal#open">Nom</button>
//     <template data-user-detail-modal-target="template">...</template>
//   </div>
export default class extends Controller {
  static targets = ["template"]

  static values = {
    animationDuration: { type: Number, default: 200 }
  }

  connect() {
    this.handleEscape = this.handleEscape.bind(this)
    this.activeModal = null
    this.previouslyFocusedElement = null
  }

  disconnect() {
    this.cleanup()
  }

  open(event) {
    event.preventDefault()

    // Empêcher un double-open
    if (this.activeModal) return
    if (!this.hasTemplateTarget) return

    this.previouslyFocusedElement = document.activeElement

    // Cloner le template et l'insérer dans le DOM
    const clone = this.templateTarget.content.cloneNode(true)
    this.activeModal = clone.querySelector('[role="dialog"]')

    document.body.appendChild(clone)

    // Attacher les événements (le contenu cloné est hors du scope Stimulus)
    this.bindModalEvents()

    // Bloquer le scroll (classe TailwindCSS, pas de style inline)
    document.body.classList.add("overflow-hidden")
    document.addEventListener("keydown", this.handleEscape)

    this.setupFocusTrap()

    // Animer l'ouverture
    requestAnimationFrame(() => {
      const content = this.activeModal?.querySelector("[data-modal-content]")
      if (content) {
        content.classList.remove("scale-95", "opacity-0")
        content.classList.add("scale-100", "opacity-100")
      }

      setTimeout(() => this.focusFirstElement(), this.animationDurationValue)
    })
  }

  close(event) {
    event?.preventDefault()
    event?.stopPropagation()

    if (!this.activeModal) return

    const content = this.activeModal.querySelector("[data-modal-content]")
    if (content) {
      content.classList.remove("scale-100", "opacity-100")
      content.classList.add("scale-95", "opacity-0")
    }

    setTimeout(() => this.cleanup(), this.animationDurationValue)
  }

  // --- Private ---

  // Attache les click handlers sur le contenu cloné (hors scope Stimulus)
  bindModalEvents() {
    if (!this.activeModal) return

    // Backdrop: clic ferme le modal
    const backdrop = this.activeModal.querySelector("[data-modal-backdrop]")
    backdrop?.addEventListener("click", () => this.close())

    // Boutons de fermeture
    this.activeModal.querySelectorAll("[data-modal-close]").forEach(button => {
      button.addEventListener("click", (e) => this.close(e))
    })

    // Empêcher la propagation du clic sur le contenu (évite fermeture via backdrop)
    const content = this.activeModal.querySelector("[data-modal-content]")
    content?.addEventListener("click", (e) => e.stopPropagation())
  }

  handleEscape(event) {
    if (event.key === "Escape") {
      this.close(event)
    }
  }

  setupFocusTrap() {
    if (!this.activeModal) return

    this.handleFocusTrap = (event) => {
      if (event.key !== "Tab") return

      const focusable = this.getFocusableElements()
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    this.activeModal.addEventListener("keydown", this.handleFocusTrap)
  }

  getFocusableElements() {
    if (!this.activeModal) return []

    return Array.from(
      this.activeModal.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
    ).filter(el => el.offsetWidth > 0 && el.offsetHeight > 0)
  }

  focusFirstElement() {
    const elements = this.getFocusableElements()
    if (elements.length > 0) elements[0].focus()
  }

  cleanup() {
    document.removeEventListener("keydown", this.handleEscape)
    document.body.classList.remove("overflow-hidden")

    if (this.activeModal) {
      this.activeModal.removeEventListener("keydown", this.handleFocusTrap)
      this.activeModal.remove()
      this.activeModal = null
    }

    if (this.previouslyFocusedElement?.focus) {
      this.previouslyFocusedElement.focus()
      this.previouslyFocusedElement = null
    }
  }
}
